import { applyFilters, type OpsBet, type OpsCallback, type OpsTx } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtMoney, fmtOdds, fmtTime, hrefWith, param, useCtx } from '../lib.ts';

/** What each recorded callback outcome means (shown on hover). */
const CB_TEXT: Record<string, string> = {
  OK: 'Answered and signed correctly.',
  TIMEOUT: 'No answer in time (outcome unknown; before 2026-10-06 11:52 UTC this label also covered 5xx and unsigned answers).',
  HTTP_5XX: 'The operator answered HTTP 5xx: outcome unknown, retried; a refusal must be HTTP 200 + signed REJECTED.',
  UNSIGNED_RESPONSE: 'HTTP 200 without a valid response signature: not trusted, outcome unknown.',
  INVALID_RESPONSE: 'Signed answer that does not match the protocol schema: outcome unknown.',
  REDIRECT: 'The operator answered with a redirect (never followed): outcome unknown.',
  NOT_SENT: 'Not sent: the circuit to this operator was open after repeated failures (no effect at the operator).',
  ERROR: 'Unexpected error on our side; retried.',
};

export function Bets() {
  const { data, filters, search } = useCtx();
  const state = param(search, 'state');
  const funding = param(search, 'funding');
  const player = param(search, 'player');
  const round = param(search, 'round');
  const rows = applyFilters(data, filters).bets.filter(
    (b) => (!state || b.bet.state === state) && (!funding || b.bet.funding === funding) && (!player || b.playerRef === player) && (!round || b.bet.gameRoundId === round),
  );
  const extra = [state && `state ${state}`, funding && `funding ${funding}`, player && `player ${player}`, round && `round ${round}`].filter(Boolean).join(', ');
  return (
    <>
      <PageHead title="Bets">{extra ? `Also filtered by ${extra}.` : 'All bets in scope.'} Rejected bets that never reached the ledger are not listed.</PageHead>
      <DataTable<OpsBet>
        testId="bets-table"
        rows={rows}
        rowKey={(b) => b.key}
        empty="No bets match these filters."
        exportName="bets"
        columns={[
          { key: 'bet', header: 'Bet', cell: (b) => <Link to={hrefWith(`/bets/${b.key}`, filters)}>{b.bet.betId}</Link>, csv: (b) => b.bet.betId },
          { key: 'player', header: 'Player', cell: (b) => <Link to={hrefWith(`/players/${b.playerRef}`, filters)}>{b.playerRef}</Link>, csv: (b) => b.playerRef },
          { key: 'brand', header: 'Brand', cell: (b) => b.brandId, csv: (b) => b.brandId },
          { key: 'round', header: 'Round', cell: (b) => <Link to={hrefWith(`/rounds/${b.bet.gameRoundId}`, filters)}>{b.bet.gameRoundId}</Link>, csv: (b) => b.bet.gameRoundId },
          { key: 'sel', header: 'Selection', cell: (b) => b.bet.selection, csv: (b) => b.bet.selection },
          { key: 'stake', header: 'Stake', numeric: true, cell: (b) => fmtMoney(b.bet.stake), csv: (b) => fmtMoney(b.bet.stake) },
          { key: 'funding', header: 'Funding', cell: (b) => (b.bet.funding === 'FREEBET' ? 'Freebet (promo)' : 'Cash'), csv: (b) => b.bet.funding },
          { key: 'state', header: 'State', cell: (b) => <StateTag state={b.bet.state} />, csv: (b) => b.bet.state },
          { key: 'credit', header: 'Credit', numeric: true, cell: (b) => (b.bet.payoutGross ? fmtMoney(b.bet.payoutGross) : '—'), csv: (b) => (b.bet.payoutGross ? fmtMoney(b.bet.payoutGross) : '') },
          { key: 'at', header: 'Submitted', cell: (b) => fmtTime(b.bet.submittedAt), csv: (b) => b.bet.submittedAt },
        ]}
      />
    </>
  );
}

function callbacksFor(cbs: readonly OpsCallback[], txKey: string) {
  return cbs.filter((c) => c.txKey === txKey);
}

export function CallbackList({ items }: { items: OpsCallback[] }) {
  if (items.length === 0) return <span className="muted">no callback</span>;
  return (
    <ul className="plain">
      {items.map((c) => (
        <li key={c.key}>
          #{c.attempt} {c.endpoint}{' '}
          <span className={`tag cb-${c.outcome}`} title={CB_TEXT[c.detail ?? c.outcome] ?? undefined}>
            {c.detail ?? c.outcome}
          </span> {c.latencyMs !== null ? `${c.latencyMs} ms` : ''} <span className="mono">{c.requestId}</span>
          {c.simulated && <span className="muted"> (simulated)</span>}
        </li>
      ))}
    </ul>
  );
}

export function BetDetail({ betKey }: { betKey: string }) {
  const { data, filters } = useCtx();
  const b = data.bets.find((x) => x.key === betKey);
  if (!b) return <p className="empty">Bet not found in your scope.</p>;
  const round = data.rounds.find((r) => r.gameRoundId === b.bet.gameRoundId);
  const txs = data.txs.filter((x) => x.betKey === b.key);
  const pendingCb = callbacksFor(data.callbacks, `pending:${b.key}`);
  const q = b.bet.quote;
  return (
    <>
      <PageHead title={`Bet ${b.bet.betId}`}>
        Drilldown: round → bet → wallet transactions → callback attempts → evidence. Read-only: there is no button to change a result or a balance.
      </PageHead>
      <dl className="kv" data-testid="bet-detail">
        <dt>Player</dt>
        <dd>
          <Link to={hrefWith(`/players/${b.playerRef}`, filters)}>{b.playerRef}</Link> · {b.operatorId} / {b.brandId}
        </dd>
        <dt>Round</dt>
        <dd>
          <Link to={hrefWith(`/rounds/${b.bet.gameRoundId}`, filters)} testId="bet-round-link">
            {b.bet.gameRoundId}
          </Link>{' '}
          {round && <StateTag state={round.state} />}
        </dd>
        <dt>Selection</dt>
        <dd>{b.bet.selection}</dd>
        <dt>Stake</dt>
        <dd>
          {fmtMoney(b.bet.stake)} · {b.bet.funding === 'FREEBET' ? `freebet, cash debited ${fmtMoney(b.bet.cashDebit)}` : 'cash'}
        </dd>
        <dt>State</dt>
        <dd>
          <StateTag state={b.bet.state} />
        </dd>
        <dt>Accepted quote</dt>
        <dd>
          {q
            ? `${fmtOdds(q.odds)} (${q.oddsVersion})${q.lightning ? ` · Lightning ${q.lightningTier} ${fmtOdds(q.lightning)}` : ''}${q.boostBps ? ` · boost +${q.boostBps / 100}%` : ''} · cap ${q.payoutCap ? fmtMoney(q.payoutCap) : '—'} · ${q.promoPolicyVersion}`
            : '—'}
        </dd>
        <dt>Credit / net</dt>
        <dd>
          {b.bet.payoutGross ? fmtMoney(b.bet.payoutGross) : '—'} / {b.bet.payoutNet ? fmtMoney(b.bet.payoutNet) : '—'}
        </dd>
      </dl>
      <Section title="Wallet transactions and callback attempts" testId="bet-txs">
        <DataTable<OpsTx>
          rows={txs}
          rowKey={(x) => x.key}
          empty="No ledger entries (freebet without cash debit, or not yet credited)."
          columns={[
            { key: 'id', header: 'Transaction', cell: (x) => <span className="mono">{x.transactionId}</span> },
            { key: 'op', header: 'Operation', cell: (x) => x.operation },
            { key: 'amt', header: 'Amount', numeric: true, cell: (x) => fmtMoney(x.amount) },
            { key: 'st', header: 'State', cell: (x) => <StateTag state={x.state} /> },
            { key: 'at', header: 'At', cell: (x) => fmtTime(x.at) },
            { key: 'cb', header: 'Callbacks', cell: (x) => <CallbackList items={callbacksFor(data.callbacks, x.key)} /> },
          ]}
        />
        {pendingCb.length > 0 && (
          <p className="warn-box">
            Credit pending at the operator wallet: <CallbackList items={pendingCb} />
          </p>
        )}
      </Section>
      {round && (
        <Section title="Evidence">
          <Link to={hrefWith(`/rounds/${round.gameRoundId}`, filters, { tab: 'evidence' })} testId="bet-evidence-link">
            Round evidence ({round.evidence?.status ?? 'not available yet'}) →
          </Link>
        </Section>
      )}
    </>
  );
}

export function Transactions() {
  const { data, filters } = useCtx();
  const rows = applyFilters(data, filters).txs;
  return (
    <>
      <PageHead title="Wallet transactions">Demo ledger entries (debit = /bet, credit = /win incl. zero-amount losses, rollback = /rollback). Callback transport is simulated in the demo.</PageHead>
      <DataTable<OpsTx>
        testId="tx-table"
        rows={rows}
        rowKey={(x) => x.key}
        empty="No transactions match these filters."
        exportName="wallet-transactions"
        columns={[
          { key: 'id', header: 'Transaction', cell: (x) => <span className="mono">{x.transactionId}</span>, csv: (x) => x.transactionId },
          { key: 'player', header: 'Player', cell: (x) => x.playerRef, csv: (x) => x.playerRef },
          { key: 'brand', header: 'Brand', cell: (x) => x.brandId, csv: (x) => x.brandId },
          { key: 'op', header: 'Operation', cell: (x) => x.operation, csv: (x) => x.operation },
          { key: 'amt', header: 'Amount', numeric: true, cell: (x) => fmtMoney(x.amount), csv: (x) => fmtMoney(x.amount) },
          { key: 'st', header: 'State', cell: (x) => <StateTag state={x.state} />, csv: (x) => x.state },
          { key: 'bet', header: 'Bet', cell: (x) => (x.betKey ? <Link to={hrefWith(`/bets/${x.betKey}`, filters)}>open</Link> : '—'), csv: (x) => x.betKey ?? '' },
          { key: 'cb', header: 'Callbacks', cell: (x) => <CallbackList items={callbacksFor(data.callbacks, x.key)} />, csv: (x) => callbacksFor(data.callbacks, x.key).map((c) => `${c.attempt}:${c.detail ?? c.outcome}`).join(' ') },
          { key: 'at', header: 'At', cell: (x) => fmtTime(x.at), csv: (x) => x.at },
        ]}
      />
    </>
  );
}
